import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import reactHtmlParser from 'react-html-parser';
import Page from '../../components/Page';
import PageHeadline from '../../components/PageHeadline';

export type ContactPageLayoutProps = StandardDivProps<ContactPageLayoutClassKey>;

export type ContactPageLayoutClassKey = 'root' | 'content' | 'contacts' | 'contactNumber' | 'map' | 'eet';

const themeSettings = { name: 'ContactPageLayout' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ContactPageLayoutClassKey, {}>({
      root: {},
      content: {
        display: 'grid',
        gridTemplateAreas: `
          "contacts  map"
          "eet       eet"
        `,
        gridTemplateColumns: '1fr 2fr',
        padding: theme.spacing(2, 0),
        width: '100%',
        overflow: 'hidden',
        '& a': {
          color: theme.palette.primary.main,
        },
        '& p': {
          display: 'flex',
          margin: 0,
        },
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(2, 1),
          gridTemplateAreas: `
          "map"
          "contacts"
          "eet"
        `,
          gridTemplateColumns: '1fr',
        },
      },
      contacts: { gridArea: 'contacts' },
      contactNumber: {
        padding: theme.spacing(1, 0),
        '& span': { width: 60 },
        '& a': {
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        },
      },
      map: {
        gridArea: 'map',
        '& .leaflet-container': {
          height: '100%',
          width: '100%',
        },
        [theme.breakpoints.down('sm')]: {
          height: 300,
        },
      },
      eet: { gridArea: 'eet' },
    }),
  themeSettings,
);

declare const L: any;

const ContactPageLayout = React.forwardRef<HTMLDivElement, ContactPageLayoutProps>(
  ({ className, classes: pClasses }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const mapRef = useRef<HTMLDivElement | null>(null);
    const leafletMapRef = useRef<any>(null);

    useEffect(() => {
      if (mapRef.current) {
        if (leafletMapRef.current) {
          leafletMapRef.current.remove(); // Odstranění existující mapy
          leafletMapRef.current = null;
        }

        const map = L.map(mapRef.current).setView([50.6529873, 14.0253268], 16);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap contributors',
        }).addTo(map);

        L.marker([50.6529873, 14.0253268])
          .addTo(map)
          .bindPopup('Storex FST, spol. s r.o.<br>Žižkova 864/73<br>400 01 Ústí nad Labem')
          .openPopup();

        leafletMapRef.current = map;
      }
    }, []);

    const pageTitle = 'Kontaktní informace';
    return (
      <Page ref={ref} className={clsx(classes.root, className)} pageTitle={pageTitle}>
        <PageHeadline>{pageTitle}</PageHeadline>
        <div className={classes.content}>
          <div className={classes.contacts}>
            <h3>Storex FST, spol. s r.o.</h3>
            <p>Žižkova 864/73</p>
            <p>400 01 Ústí nad Labem</p>
            <div className={classes.contactNumber}>{reactHtmlParser(contactDetails)}</div>
          </div>
          <div className={classes.map}>
            <div ref={mapRef} className={classes.map}></div>
          </div>
          <div className={classes.eet}>{reactHtmlParser(eet)}</div>
        </div>
      </Page>
    );
  },
);

ContactPageLayout.displayName = 'ContactPageLayout';

export default withThemeProps(themeSettings)(ContactPageLayout);

const contactDetails = `<p><span>IČ: </span>63146801</p><p><span>DIČ: </span>CZ63146801</p><p><span>Tel.:</span><a href="tel:+420606650272"> +420 606 650 272</a></p><p><span>Email:</span> <a href="mailto:zbozi@storex.cz">zbozi@storex.cz</a></p>`;
const eet = `<h3>EET</h3><p>Podle zákona o evidenci tržeb je prodávající povinen vystavit kupujícímu účtenku. Zároveň je povinen zaevidovat přijatou tržbu u správce daně online; v případě technického výpadku pak nejpozději do 48 hodin.</p>`;
